<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import { extractErrorMessage } from '~/plugins/vue-plugins/fetch-error-handler'
import type { AppErrorMessage, AppFetchError } from '~/plugins/vue-plugins/fetch-error-handler'
import AppDirectives from '~/plugins/vue-plugins/directives'
import DebugHintIcon from '~/components/Common/DebugHintIcon.vue'
import ContentCopy from '~/components/Common/ContentCopy.vue'

/**
 * Used for a uniform display of a try-catch-Error as a result of a XHR
 */
export default defineComponent({
  name: 'FetchErrorRetry',
  components: { ContentCopy, DebugHintIcon },
  directives: AppDirectives,
  props: {
    error: {
      required: true,
      type: [Object, String, Error] as PropType<AppFetchError>,
    },
    retry: {
      required: false,
      type: [Function, Object] as PropType<(() => void) | null>,
      default: null,
      validator(value) {
        return typeof value === 'function'
      },
    },
  },
  data(): { errorMessage: AppErrorMessage } {
    return {
      errorMessage: {
        ofError: '',
        ofResponse: [],
      },
    }
  },
  watch: {
    error: {
      immediate: true,
      async handler(error: AppFetchError) {
        this.errorMessage = await extractErrorMessage(error)
      },
    },
  },
})
</script>

<template>
  <v-alert border="start" type="error" variant="tonal" :rounded="false" icon="mdi-alert">
    <div class="font-weight-bold">
      {{ $t('FetchErrorTitle') }}
    </div>

    <small>
      {{ errorMessage.ofError }}

      <!-- Details -->
      <v-tooltip :open-delay="0" interactive attach location="left">
        <template #activator="{ props }">
          <v-btn-icon variant="plain" size="x-small" v-bind="props" icon="mdi-information-outline"> </v-btn-icon>
        </template>

        <v-sheet max-width="400">
          <!-- Of Response -->
          <template v-for="(msg, index) in errorMessage.ofResponse" :key="index">
            {{ msg }}
            <br />
          </template>

          <!-- Error Stack -->
          <div v-if="errorMessage.stack" class="overflow-auto">
            <pre>{{ errorMessage.stack }}</pre>
          </div>
          <v-divider />

          <!-- Copy -->
          <div class="d-flex justify-end">
            <ContentCopy :content="[errorMessage.stack, ...errorMessage.ofResponse]"></ContentCopy>
          </div>
        </v-sheet>
      </v-tooltip>
    </small>

    <!-- Actions -->
    <div class="d-flex mt-3">
      <DebugHintIcon :to-json="errorMessage"></DebugHintIcon>

      <v-spacer></v-spacer>

      <!-- Retry -->
      <v-btn v-if="typeof retry === 'function'" color="surface" prepend-icon="mdi-refresh" @click="retry">
        {{ $t('FetchErrorRetry') }}
      </v-btn>
    </div>
  </v-alert>
</template>

<style scoped lang="scss"></style>
